import { useState, useEffect, useRef } from 'react';
import { getEquipePublique, setApparence, uploadLogo, deleteLogo, urlLogo, getCurrentUser,
         getEquipesClub, creerEquipe, majEquipe, rattacher, detacher,
         getComptesClub, creerCompteStaff, majCompteClub, resetCompteClub, setVisibiliteStats } from '../api';
import { calculerTheme, hexValide, DEFAUT } from '../theme';

/**
 * Espace du club — apparence.
 * L'aperçu est appliqué à un cadre isolé : le reste de l'application ne change
 * qu'à l'enregistrement, pour que l'admin puisse essayer sans tout faire clignoter.
 */
function Apparence() {
  const superAdmin = getCurrentUser()?.role === 'super_admin';
  const [club, setClub]       = useState(null);
  const [c1, setC1]           = useState(DEFAUT.couleur);
  const [c2, setC2]           = useState('#ffffff');
  const [avecC2, setAvecC2]   = useState(false);
  const [logo, setLogo]       = useState(null);
  const [envoi, setEnvoi]     = useState(false);
  const [enreg, setEnreg]     = useState(false);
  const [toast, setToast]     = useState(null);
  const fichier = useRef();

  const charger = () => getEquipePublique().then(e => {
    setClub(e);
    setC1(hexValide(e.couleur) ? e.couleur : DEFAUT.couleur);
    setAvecC2(hexValide(e.couleur2));
    if (hexValide(e.couleur2)) setC2(e.couleur2);
    setLogo(urlLogo(e.logo_url));
  });
  useEffect(() => { charger(); }, []);

  const notifier = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3200); };
  const clubId = superAdmin ? club?.club_id : undefined;
  const t = calculerTheme(c1, avecC2 ? c2 : null);
  const modifie = club && (c1 !== (club.couleur || DEFAUT.couleur) || (avecC2 ? c2 : null) !== (club.couleur2 || null));

  async function enregistrer() {
    setEnreg(true);
    try {
      await setApparence(c1, avecC2 ? c2 : null, clubId);
      notifier('Couleurs enregistrées');
      await charger();
      window.dispatchEvent(new Event('club-maj'));
    } catch (e) { notifier(e.message, 'error'); }
    finally { setEnreg(false); }
  }

  function inverser() { const a = c1; setC1(c2); setC2(a); setAvecC2(true); }

  async function choisirLogo(e) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setEnvoi(true);
    try {
      const r = await uploadLogo(f, clubId);
      setLogo(urlLogo(r.logo_url));
      notifier('Logo mis à jour');
      window.dispatchEvent(new Event('club-maj'));
    } catch (err) { notifier(err.message, 'error'); }
    finally { setEnvoi(false); }
  }

  async function retirerLogo() {
    if (!window.confirm('Retirer le logo du club ?')) return;
    try { await deleteLogo(clubId); setLogo(null); notifier('Logo retiré'); window.dispatchEvent(new Event('club-maj')); }
    catch (err) { notifier(err.message, 'error'); }
  }

  if (!club) return <div style={{ padding:40, textAlign:'center', color:'var(--muted)' }}>Chargement…</div>;

  // Variables appliquées au seul cadre d'aperçu
  const varsApercu = {
    '--accent': t.accent, '--accent-rgb': t.accentRgb, '--accent-text': t.accentText,
    '--accent2': t.accent2, '--chart2': t.chart2, '--red': t.accent
  };

  return (
    <>
      <div className="section">

        {/* ── Couleurs ──────────────────────────────────────────────── */}
        <div className="section-title">Couleurs</div>
        <div className="card" style={{ marginBottom:14 }}>
          <Choix libelle="Couleur principale"
                 aide="Boutons, onglets, titres, courbe principale des graphiques"
                 valeur={c1} onChange={setC1} />

          <div style={{ borderTop:'1px solid var(--border)', margin:'14px 0' }} />

          <label style={{ display:'flex', alignItems:'center', gap:9, cursor:'pointer', fontSize:13, fontWeight:600 }}>
            <input type="checkbox" checked={avecC2} onChange={e => setAvecC2(e.target.checked)}
                   style={{ width:17, height:17, accentColor:'var(--accent)' }} />
            Ajouter une couleur secondaire
          </label>
          {avecC2 && (
            <div style={{ marginTop:12 }}>
              <Choix libelle="Couleur secondaire"
                     aide="Liseré des en-têtes, seconde courbe des graphiques"
                     valeur={c2} onChange={setC2} />
            </div>
          )}

          {t.remarques.length > 0 && (
            <div style={{ marginTop:14, background:'rgba(234,179,8,.08)', border:'1px solid rgba(234,179,8,.3)',
                          borderRadius:9, padding:'11px 13px' }}>
              {t.remarques.map((r, i) => (
                <div key={i} style={{ fontSize:12, color:'#eab308', lineHeight:1.55, marginTop: i ? 6 : 0 }}>ℹ️ {r}</div>
              ))}
              {t.remarques.some(r => r.includes('Inverser')) && (
                <button type="button" onClick={inverser}
                  style={{ marginTop:9, background:'none', border:'1px solid rgba(234,179,8,.45)', borderRadius:7,
                           color:'#eab308', fontSize:11.5, fontWeight:700, padding:'6px 11px', cursor:'pointer' }}>
                  ⇄ Inverser les deux couleurs
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Aperçu ────────────────────────────────────────────────── */}
        <div className="section-title">Aperçu</div>
        <div style={{ ...varsApercu, marginBottom:14 }}>
          <Apercu nom={club.club} logo={logo} t={t} />
        </div>

        <div style={{ display:'flex', gap:9, marginBottom:26, flexWrap:'wrap' }}>
          <button className="btn btn-primary" onClick={enregistrer} disabled={!modifie || enreg}
                  style={{ width:'auto', padding:'11px 22px', opacity: !modifie || enreg ? .5 : 1 }}>
            {enreg ? 'Enregistrement…' : 'Enregistrer les couleurs'}
          </button>
          <button className="btn btn-secondary" style={{ width:'auto', padding:'11px 16px' }}
                  onClick={() => { setC1(DEFAUT.couleur); setAvecC2(false); }}>
            Couleurs d'origine
          </button>
        </div>

        {/* ── Logo ──────────────────────────────────────────────────── */}
        <div className="section-title">Logo</div>
        <div className="card">
          <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
            <div style={{ width:84, height:84, borderRadius:14, background:'var(--bg)', border:'1px solid var(--border)',
                          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, overflow:'hidden' }}>
              {logo ? <img src={logo} alt="Logo du club" style={{ maxWidth:'86%', maxHeight:'86%', objectFit:'contain' }} />
                    : <img src="/img/symbole-equipote.png" alt="" style={{ width:'60%', opacity:.35 }} />}
            </div>
            <div style={{ flex:1, minWidth:180 }}>
              <div style={{ fontSize:12.5, color:'var(--muted)', lineHeight:1.6, marginBottom:10 }}>
                Image carrée de préférence, avec un fond transparent.<br/>
                PNG, JPEG ou WebP. Elle est automatiquement réduite avant l'envoi.
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button className="btn btn-primary" style={{ width:'auto', padding:'9px 16px' }}
                        onClick={() => fichier.current?.click()} disabled={envoi}>
                  {envoi ? 'Envoi…' : logo ? 'Changer le logo' : 'Ajouter un logo'}
                </button>
                {logo && <button className="btn btn-secondary" style={{ width:'auto', padding:'9px 14px' }}
                                 onClick={retirerLogo}>Retirer</button>}
              </div>
              <input ref={fichier} type="file" accept="image/png,image/jpeg,image/webp"
                     onChange={choisirLogo} style={{ display:'none' }} />
            </div>
          </div>
        </div>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

/** Sélecteur de couleur : nuancier natif + saisie du code. */
function Choix({ libelle, aide, valeur, onChange }) {
  const [texte, setTexte] = useState(valeur);
  useEffect(() => setTexte(valeur), [valeur]);
  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700 }}>{libelle}</div>
      <div style={{ fontSize:11.5, color:'var(--muted)', margin:'2px 0 9px' }}>{aide}</div>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <input type="color" value={valeur} onChange={e => onChange(e.target.value)}
               aria-label={libelle}
               style={{ width:46, height:40, border:'1px solid var(--border)', borderRadius:9,
                        background:'none', padding:2, cursor:'pointer' }} />
        <input type="text" value={texte} maxLength={7} spellCheck={false}
               onChange={e => { const v = e.target.value.trim(); setTexte(v); if (hexValide(v)) onChange(v.toLowerCase()); }}
               style={{ width:110, background:'var(--bg)', border:`1px solid ${hexValide(texte) ? 'var(--border)' : 'var(--danger)'}`,
                        borderRadius:9, padding:'9px 11px', color:'var(--text)', fontFamily:'monospace', fontSize:13 }} />
      </div>
    </div>
  );
}

/** Miniature de l'application avec les couleurs choisies. */
function Apercu({ nom, logo, t }) {
  const pts = [[0,30],[1,24],[2,28],[3,33],[4,29]], adv = [[0,26],[1,27],[2,22],[3,25],[4,24]];
  const ligne = d => d.map(([x, y], i) => `${i ? 'L' : 'M'}${14 + x * 62},${70 - (y - 18) * 3.2}`).join(' ');
  return (
    <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:14, overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 13px', borderBottom:'1px solid var(--border)' }}>
        <img src={logo || '/img/symbole-equipote.png'} alt="" style={{ height:22, width:22, objectFit:'contain', borderRadius:4 }} />
        <span style={{ fontFamily:'Barlow Condensed', fontWeight:800, fontSize:15, letterSpacing:.5 }}>{(nom || '').toUpperCase()}</span>
      </div>
      <div style={{ padding:'13px 14px', borderBottom:'2px solid var(--accent)',
                    borderImage:'linear-gradient(90deg, var(--accent), var(--accent2)) 1' }}>
        <div className="eyebrow">Saison 2026/2027</div>
        <div style={{ fontFamily:'Barlow Condensed', fontSize:22, fontWeight:800 }}>Statistiques</div>
      </div>
      <div style={{ display:'flex', gap:18, padding:'0 14px', borderBottom:'1px solid var(--border)' }}>
        <span style={{ padding:'9px 0', fontSize:11, fontWeight:700, color:'var(--accent)',
                       borderBottom:'2px solid var(--accent)', letterSpacing:1 }}>SAISON</span>
        <span style={{ padding:'9px 0', fontSize:11, fontWeight:700, color:'var(--muted)', letterSpacing:1 }}>MATCH</span>
      </div>
      <div style={{ padding:14 }}>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:12 }}>
          <button className="btn btn-primary" style={{ width:'auto', padding:'8px 14px', fontSize:12 }}>Je serai là</button>
          <span className="badge badge-V">Victoire</span>
          <span className="badge badge-D">Défaite</span>
          <span style={{ fontFamily:'Barlow Condensed', fontSize:20, fontWeight:800, color:'var(--accent)' }}>34 buts</span>
        </div>
        <svg viewBox="0 0 280 80" style={{ width:'100%', height:80, display:'block' }} aria-hidden="true">
          <path d={ligne(adv)} fill="none" stroke={t.chart2} strokeWidth="2" />
          <path d={ligne(pts)} fill="none" stroke={t.accent} strokeWidth="2.5" />
        </svg>
        <div style={{ fontSize:10.5, color:'var(--muted)', marginTop:6 }}>
          La défaite reste en rouge quelle que soit la couleur choisie : c'est une information, pas une décoration.
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Page à onglets
   ═══════════════════════════════════════════════════════════════════════════ */
export default function ClubPage() {
  const [onglet, setOnglet] = useState('equipes');
  const [club, setClub] = useState(null);
  useEffect(() => { getEquipePublique().then(setClub).catch(() => {}); }, []);
  const superAdmin = getCurrentUser()?.role === 'super_admin';
  const clubId = superAdmin ? club?.club_id : undefined;

  return (
    <>
      <div className="page-header">
        <div className="eyebrow">Espace du club</div>
        <h1>🏛️ {club?.club || 'Mon club'}</h1>
        <div className="sub">Équipes, encadrement et apparence de l'application</div>
      </div>
      <div className="tabs">
        {[['equipes','👥 Équipes'],['encadrement','🎽 Encadrement'],['apparence','🎨 Apparence']].map(([k,l]) => (
          <button key={k} className={`tab-btn ${onglet===k?'active':''}`} onClick={() => setOnglet(k)}>{l}</button>
        ))}
      </div>
      {club && onglet === 'equipes'     && <Equipes clubId={clubId} club={club} onMaj={() => getEquipePublique().then(setClub)} />}
      {club && onglet === 'encadrement' && <Encadrement clubId={clubId} />}
      {club && onglet === 'apparence'   && <Apparence />}
    </>
  );
}

const ROLES_STAFF = [
  { key:'admin_club', label:'Admin du club', desc:'Gère tout le club : équipes, encadrement, apparence' },
  { key:'coach',      label:'Coach',         desc:'Effectif, convocations, présences et import des feuilles de ses équipes' },
  { key:'tresorier',  label:'Trésorier',     desc:'Gages d\'équipe et cagnotte de ses équipes' },
];
const libelleRole = r => ({ admin_club:'Admin du club', admin:'Admin du club', coach:'Coach',
                            tresorier:'Trésorier', admin_3mt:'Trésorier', joueur:'Joueur' })[r] || r;
const champ = { width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:9,
                padding:'10px 12px', color:'var(--text)', fontSize:14 };
function useToast() {
  const [t, setT] = useState(null);
  return [t, (msg, type = 'success') => { setT({ msg, type }); setTimeout(() => setT(null), 3200); }];
}

/* ── Équipes ─────────────────────────────────────────────────────────────── */
function Equipes({ clubId, club, onMaj }) {
  const [equipes, setEquipes] = useState([]);
  const [form, setForm]       = useState(null);
  const [toast, notifier]     = useToast();
  const charger = () => getEquipesClub(clubId).then(setEquipes).catch(e => notifier(e.message, 'error'));
  useEffect(() => { charger(); }, [clubId]);

  async function enregistrer(e) {
    e.preventDefault();
    try {
      if (form.id) await majEquipe(form.id, { nom: form.nom, categorie: form.categorie, actif: true });
      else await creerEquipe({ nom: form.nom, categorie: form.categorie, ...(clubId ? { club_id: clubId } : {}) });
      notifier(form.id ? 'Équipe modifiée' : 'Équipe créée — son barème de gages est prêt');
      setForm(null); charger();
    } catch (err) { notifier(err.message, 'error'); }
  }

  async function archiver(e) {
    if (!window.confirm(`Archiver ${e.nom} ? Ses données sont conservées, mais elle n'apparaîtra plus.`)) return;
    try { await majEquipe(e.id, { ...e, actif: false }); notifier('Équipe archivée'); charger(); }
    catch (err) { notifier(err.message, 'error'); }
  }

  const actives = equipes.filter(e => e.actif);
  return (
    <div className="section">
      {!form && <button className="btn btn-primary" style={{ marginBottom:16 }}
                        onClick={() => setForm({ nom:'', categorie:'' })}>+ Nouvelle équipe</button>}
      {form && (
        <form className="card" style={{ marginBottom:16 }} onSubmit={enregistrer}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--accent)', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>
            {form.id ? 'Modifier l\'équipe' : 'Nouvelle équipe'}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:9, marginBottom:12 }}>
            <input style={champ} placeholder="Nom — ex. Seniors M 2" value={form.nom}
                   onChange={e => setForm({ ...form, nom: e.target.value })} required autoFocus />
            <input style={champ} placeholder="Catégorie — ex. -18 F" value={form.categorie || ''}
                   onChange={e => setForm({ ...form, categorie: e.target.value })} />
          </div>
          <div style={{ display:'flex', gap:9 }}>
            <button className="btn btn-primary">Enregistrer</button>
            <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Annuler</button>
          </div>
        </form>
      )}

      <div className="section-title">{actives.length} équipe{actives.length > 1 ? 's' : ''}</div>
      {actives.map(e => (
        <div key={e.id} className="card card-row" style={{ marginBottom:8 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:700 }}>{e.nom}</div>
            <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:2 }}>
              {e.categorie ? `${e.categorie} · ` : ''}{e.nb_joueurs} joueur{e.nb_joueurs > 1 ? 's' : ''}</div>
          </div>
          <div style={{ display:'flex', gap:6 }}>
            <button className="btn btn-secondary" style={{ width:'auto', padding:'7px 11px', fontSize:11.5 }}
                    onClick={() => setForm({ id: e.id, nom: e.nom, categorie: e.categorie })}>✏️</button>
            {actives.length > 1 && <button className="btn btn-secondary" style={{ width:'auto', padding:'7px 11px', fontSize:11.5 }}
                    onClick={() => archiver(e)}>Archiver</button>}
          </div>
        </div>
      ))}
      <div className="section-title" style={{ marginTop:24 }}>Visibilité des statistiques</div>
      <div className="card">
        <label style={{ display:'flex', gap:11, alignItems:'flex-start', cursor:'pointer' }}>
          <input type="checkbox" checked={!!club?.stats_publiques} style={{ width:19, height:19, marginTop:2, accentColor:'var(--accent)' }}
            onChange={async e => {
              const pub = e.target.checked;
              if (pub && !window.confirm("Rendre les statistiques consultables par n'importe qui, sans connexion ?\n\nLes noms des joueurs seront visibles publiquement. Si vos équipes comptent des mineurs, assurez-vous d'avoir l'accord écrit de leurs parents.")) return;
              try { await setVisibiliteStats(pub, clubId); notifier(pub ? 'Statistiques publiques' : 'Statistiques réservées aux membres'); onMaj(); }
              catch (err) { notifier(err.message, 'error'); }
            }} />
          <div>
            <div style={{ fontSize:13.5, fontWeight:700 }}>Statistiques consultables sans connexion</div>
            <div style={{ fontSize:12, color:'var(--muted)', marginTop:3, lineHeight:1.6 }}>
              Désactivé, seuls les membres connectés voient les statistiques. C'est le réglage recommandé,
              surtout si vos équipes comptent des mineurs : nom, club et horaires de match permettent de les localiser.
            </div>
          </div>
        </label>
        {!!club?.stats_publiques && (
          <div style={{ marginTop:12, borderTop:'1px solid var(--border)', paddingTop:12 }}>
            <div style={{ fontSize:11.5, color:'var(--muted)', marginBottom:7 }}>
              Liens à partager — sur le site du club, les réseaux sociaux :</div>
            {actives.map(e => {
              const lien = `${window.location.origin}/?equipe=${e.id}`;
              return (
                <div key={e.id} style={{ display:'flex', gap:7, alignItems:'center', marginBottom:6 }}>
                  <span style={{ fontSize:12, minWidth:90 }}>{e.nom}</span>
                  <code style={{ flex:1, fontSize:11.5, background:'var(--bg)', borderRadius:6, padding:'6px 8px',
                                 overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lien}</code>
                  <button type="button" className="btn btn-secondary" style={{ width:'auto', padding:'6px 10px', fontSize:11 }}
                          onClick={() => { navigator.clipboard?.writeText(lien); notifier('Lien copié'); }}>Copier</button>
                </div>);
            })}
          </div>)}
      </div>

      <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:12, lineHeight:1.6 }}>
        Chaque équipe a ses propres convocations, statistiques, gages et cagnotte. Les joueurs s'ajoutent
        depuis le menu Équipe, une fois l'équipe sélectionnée dans la barre du haut.
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}

/* ── Encadrement ─────────────────────────────────────────────────────────── */
function Encadrement({ clubId }) {
  const moi = getCurrentUser();
  const [comptes, setComptes] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [form, setForm]       = useState(null);
  const [acces, setAcces]     = useState(null);
  const [toast, notifier]     = useToast();

  const charger = () => Promise.all([getComptesClub(clubId), getEquipesClub(clubId)])
    .then(([c, e]) => { setComptes(c); setEquipes(e.filter(x => x.actif)); })
    .catch(e => notifier(e.message, 'error'));
  useEffect(() => { charger(); }, [clubId]);

  async function creer(e) {
    e.preventDefault();
    try {
      const r = await creerCompteStaff({ ...form, ...(clubId ? { club_id: clubId } : {}) });
      setAcces(r.compte); setForm(null); charger();
    } catch (err) { notifier(err.message, 'error'); }
  }
  async function changerRole(c, role) {
    try { await majCompteClub(c.id, { role }); notifier('Rôle modifié'); charger(); }
    catch (err) { notifier(err.message, 'error'); }
  }
  async function basculerEquipe(c, eq, dedans) {
    try { dedans ? await detacher(eq.id, c.id) : await rattacher(eq.id, c.id); charger(); }
    catch (err) { notifier(err.message, 'error'); }
  }
  async function basculerActif(c) {
    try { await majCompteClub(c.id, { actif: !c.actif }); notifier(c.actif ? 'Compte suspendu' : 'Compte réactivé'); charger(); }
    catch (err) { notifier(err.message, 'error'); }
  }
  async function reset(c) {
    if (!window.confirm(`Générer un nouveau mot de passe pour ${c.username} ?`)) return;
    try { const r = await resetCompteClub(c.id); setAcces(r.compte); }
    catch (err) { notifier(err.message, 'error'); }
  }

  const staff = comptes.filter(c => c.role !== 'joueur');
  const joueurs = comptes.filter(c => c.role === 'joueur');

  return (
    <div className="section">
      {acces && (
        <div className="card" style={{ marginBottom:14, borderColor:'rgba(34,197,94,.45)', background:'rgba(34,197,94,.06)' }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#22c55e', letterSpacing:2, textTransform:'uppercase', marginBottom:8 }}>✓ Accès</div>
          <div style={{ fontFamily:'monospace', fontSize:13.5, lineHeight:1.8, background:'var(--bg)', borderRadius:8, padding:'10px 13px' }}>
            <div>Identifiant : <b>{acces.username}</b></div>
            <div>Mot de passe : <b style={{ color:'#22c55e' }}>{acces.password}</b></div>
          </div>
          <div style={{ fontSize:11.5, color:'var(--muted)', margin:'9px 0' }}>Envoyés par email. Ce mot de passe ne sera plus affiché.</div>
          <button className="btn btn-secondary" style={{ width:'auto', padding:'8px 14px', fontSize:12 }} onClick={() => setAcces(null)}>Fermer</button>
        </div>
      )}

      {!form && <button className="btn btn-primary" style={{ marginBottom:16 }}
                        onClick={() => setForm({ prenom:'', nom:'', email:'', role:'coach', equipes: equipes.length === 1 ? [equipes[0].id] : [] })}>
                  + Ajouter un membre de l'encadrement</button>}
      {form && (
        <form className="card" style={{ marginBottom:16 }} onSubmit={creer}>
          <div style={{ fontSize:12, fontWeight:700, color:'var(--accent)', letterSpacing:1, textTransform:'uppercase', marginBottom:12 }}>Nouveau membre</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:9, marginBottom:9 }}>
            <input style={champ} placeholder="Prénom" value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} required />
            <input style={champ} placeholder="Nom" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required />
          </div>
          <input style={{ ...champ, marginBottom:12 }} type="email" placeholder="Email — ses accès lui seront envoyés"
                 value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
          <div style={{ display:'grid', gap:6, marginBottom:12 }}>
            {ROLES_STAFF.map(r => (
              <button key={r.key} type="button" onClick={() => setForm({ ...form, role: r.key })}
                style={{ textAlign:'left', padding:'9px 12px', borderRadius:9, cursor:'pointer', background:'var(--bg)',
                         border:`1px solid ${form.role === r.key ? 'var(--accent)' : 'var(--border)'}` }}>
                <div style={{ fontSize:13, fontWeight:700, color: form.role === r.key ? 'var(--accent)' : 'var(--text)' }}>{r.label}</div>
                <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:1 }}>{r.desc}</div>
              </button>
            ))}
          </div>
          {form.role !== 'admin_club' && (
            <>
              <div style={{ fontSize:11, color:'var(--muted)', letterSpacing:1, textTransform:'uppercase', marginBottom:7 }}>Ses équipes</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                {equipes.map(eq => {
                  const on = form.equipes.includes(eq.id);
                  return <button key={eq.id} type="button"
                    onClick={() => setForm({ ...form, equipes: on ? form.equipes.filter(x => x !== eq.id) : [...form.equipes, eq.id] })}
                    style={{ padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:12.5, fontWeight:600,
                             border:`1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                             background: on ? 'rgba(var(--accent-rgb),.12)' : 'var(--bg)',
                             color: on ? 'var(--accent)' : 'var(--muted)' }}>{on ? '✓ ' : ''}{eq.nom}</button>;
                })}
              </div>
            </>
          )}
          <div style={{ display:'flex', gap:9 }}>
            <button className="btn btn-primary">Créer le compte</button>
            <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Annuler</button>
          </div>
        </form>
      )}

      <div className="section-title">Encadrement</div>
      {staff.map(c => {
        const estMoi = c.id === moi?.id || c.username === moi?.username;
        return (
          <div key={c.id} className="card" style={{ marginBottom:8, opacity: c.actif ? 1 : .55 }}>
            <div style={{ display:'flex', justifyContent:'space-between', gap:10, flexWrap:'wrap', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:14, fontWeight:700 }}>{c.prenom ? `${c.prenom} ${c.nom}` : c.username}
                  {estMoi && <span style={{ fontSize:10, color:'var(--accent)', marginLeft:7 }}>MOI</span>}
                  {!c.actif && <span style={{ fontSize:10, color:'var(--danger)', marginLeft:7 }}>SUSPENDU</span>}</div>
                <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:2 }}>{c.username}{c.email ? ` · ${c.email}` : ''}</div>
              </div>
              <select value={['admin','admin_3mt'].includes(c.role) ? { admin:'admin_club', admin_3mt:'tresorier' }[c.role] : c.role}
                      disabled={estMoi} onChange={e => changerRole(c, e.target.value)}
                      style={{ background:'var(--bg)', color:'var(--text)', border:'1px solid var(--border)', borderRadius:8, padding:'6px 8px', fontSize:12 }}>
                {ROLES_STAFF.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                <option value="joueur">Joueur</option>
              </select>
            </div>
            {!['admin_club','admin'].includes(c.role) && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginTop:10 }}>
                {equipes.map(eq => {
                  const dedans = c.equipes.some(x => x.id === eq.id);
                  return <button key={eq.id} onClick={() => basculerEquipe(c, eq, dedans)}
                    title={dedans ? 'Retirer de cette équipe' : 'Rattacher à cette équipe'}
                    style={{ padding:'4px 10px', borderRadius:7, cursor:'pointer', fontSize:11.5,
                             border:`1px solid ${dedans ? 'var(--accent)' : 'var(--border)'}`,
                             background: dedans ? 'rgba(var(--accent-rgb),.12)' : 'transparent',
                             color: dedans ? 'var(--accent)' : 'var(--muted)' }}>{dedans ? '✓ ' : '+ '}{eq.nom}</button>;
                })}
              </div>
            )}
            {!estMoi && (
              <div style={{ display:'flex', gap:6, marginTop:10 }}>
                <button className="btn btn-secondary" style={{ width:'auto', padding:'6px 10px', fontSize:11 }} onClick={() => reset(c)}>🔑 Mot de passe</button>
                <button className="btn btn-secondary" style={{ width:'auto', padding:'6px 10px', fontSize:11 }} onClick={() => basculerActif(c)}>
                  {c.actif ? 'Suspendre' : 'Réactiver'}</button>
              </div>
            )}
          </div>
        );
      })}
      <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:12, lineHeight:1.6 }}>
        {joueurs.length} joueur{joueurs.length > 1 ? 's' : ''} avec un compte, gérés depuis le menu Équipe.
        Un joueur peut aussi être promu coach ou trésorier en changeant son rôle ici.
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
